import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessMobileBrandingAPI } from '../../lib/api';
import { writeStoredUser } from '../../lib/authStorage';
import { notify } from '../../lib/notifications';
import { useAuth } from '../../lib/auth';

const APP_NAME_MAX = 40;
const LOGO_MAX_BYTES = 2 * 1024 * 1024;
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'T';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase() || 'T';
}

/**
 * Business → App branding.
 *
 * Org admins set the name and logo members see in the mobile app after
 * they sign in. The store listing stays taskydo: one binary serves every
 * organisation, so the home-screen icon cannot change per company.
 */
export default function BusinessAppBranding() {
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [appName, setAppName] = useState('');
  const [file, setFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);

  const applyPayload = (payload) => {
    setStatus(payload);
    if (payload && typeof payload.app_name === 'string') setAppName(payload.app_name);
    setFile(null);
    setRemoveLogo(false);
  };

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyPayload(await BusinessMobileBrandingAPI.get());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const organisationName = status?.organisation_name || '';
  const previewName = appName.trim() || organisationName || 'taskydo';
  const currentLogo = status?.logo_url || '';
  const logoSrc = previewUrl || (removeLogo ? '' : currentLogo);
  const editable = !!status && status.editable !== false;

  const onPickFile = (event) => {
    const next = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!next) return;
    if (next.type && !LOGO_TYPES.includes(next.type)) {
      setError({ message: 'Use a PNG, JPEG, WebP, or GIF. SVG logos are not supported.' });
      return;
    }
    if (next.size > LOGO_MAX_BYTES) {
      setError({ message: 'Logo must be 2 MB or smaller.' });
      return;
    }
    setError(null);
    setFile(next);
    setRemoveLogo(false);
  };

  const save = useCallback(async () => {
    const trimmed = appName.trim();
    if (trimmed.length > APP_NAME_MAX) {
      setError({ message: `App name must be ${APP_NAME_MAX} characters or fewer.` });
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = new FormData();
      body.append('app_name', trimmed);
      if (removeLogo && !file) body.append('remove_logo', 'true');
      if (file) body.append('logo', file, file.name);
      const payload = await BusinessMobileBrandingAPI.update(body);
      applyPayload(payload);
      if (user) {
        const nextUser = {
          ...user,
          mobile_app_name: payload.app_name,
          organisation_logo_url: payload.logo_url,
        };
        setUser(nextUser);
        writeStoredUser(nextUser);
      }
      notify.success('App branding saved. Members see it in the mobile app after they sign in.');
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }, [appName, file, removeLogo, user, setUser]);

  if (loading && !status) return <Loader label="Loading app branding…" />;

  return (
    <>
      <PageHeader
        title="🏷️ App branding"
        subtitle="The name and logo members see in the mobile app after they sign in. The App Store and Play Store listing stays taskydo."
      />

      <ErrorBox error={error} />

      <section className="ty-card">
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 12px' }}>
          Inside the mobile app
        </h2>
        <p style={{ color: 'var(--ty-muted)', marginTop: 0, marginBottom: 16 }}>
          One shared app is installed as taskydo. After a member signs in to
          this organisation, the header and home screen use the name and logo
          below. The website keeps the taskydo name.
        </p>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            marginBottom: 20,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 72,
              height: 72,
              borderRadius: 16,
              overflow: 'hidden',
              display: 'grid',
              placeItems: 'center',
              background: '#7980ff',
              color: '#fff',
              fontSize: 28,
              fontWeight: 800,
              flex: '0 0 auto',
            }}
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt=""
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials(previewName)
            )}
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{previewName}</div>
            <div style={{ fontSize: 13, color: 'var(--ty-muted)' }}>
              Preview after sign-in
            </div>
          </div>
        </div>

        <div className="ty-field">
          <label htmlFor="org-mobile-app-name">App name</label>
          <input
            id="org-mobile-app-name"
            className="ty-input"
            maxLength={APP_NAME_MAX}
            value={appName}
            disabled={!editable || saving}
            onChange={(e) => setAppName(e.target.value)}
          />
          <span style={{ fontSize: 12, color: 'var(--ty-muted)' }}>
            {organisationName
              ? `Organisation name: ${organisationName}. Leave this matching that name, or clear it, and later renames stay in sync. Max ${APP_NAME_MAX} characters.`
              : `Max ${APP_NAME_MAX} characters.`}
          </span>
        </div>

        {!!organisationName && editable && (
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            style={{ marginTop: 8 }}
            disabled={saving || appName.trim() === organisationName}
            onClick={() => setAppName(organisationName)}
          >
            Use organisation name
          </button>
        )}

        <div className="ty-field" style={{ marginTop: 16 }}>
          <label htmlFor="org-mobile-logo">Logo</label>
          <input
            id="org-mobile-logo"
            className="ty-input"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            disabled={!editable || saving}
            onChange={onPickFile}
          />
          <span style={{ fontSize: 12, color: 'var(--ty-muted)' }}>
            Square PNG or JPEG works best. Up to 2 MB. Without a logo,
            members see initials.
            {file ? ` Selected: ${file.name}.` : ''}
          </span>
        </div>

        {(!!currentLogo || !!file) && editable && (
          <button
            type="button"
            className="ty-btn ty-btn--ghost"
            disabled={saving || (removeLogo && !file)}
            onClick={() => {
              setFile(null);
              setRemoveLogo(true);
            }}
          >
            Remove logo
          </button>
        )}

        {!editable && (
          <p style={{ fontSize: 13, color: 'var(--ty-muted)', marginBottom: 0 }}>
            Only organisation admins can change this.
          </p>
        )}
      </section>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        <button
          type="button"
          className="ty-btn"
          onClick={save}
          disabled={saving || !editable}
        >
          {saving ? 'Saving…' : 'Save branding'}
        </button>
        <button
          type="button"
          className="ty-btn ty-btn--ghost"
          onClick={reload}
          disabled={saving || loading}
        >
          Discard
        </button>
      </div>
    </>
  );
}
